import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lessonContentId"];
/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useLessonQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */
import { isAcceptanceTest } from 'hub-http/helpers/env';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import I18n from 'I18n';
import { useGetAiAccessControls } from 'academy-common-lib/features/aiControl/aiAccessQueries';
import { VideoAssetTypename } from '../../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/LessonChirpService';
import { ASYNC_BULK_GET_QUIZ_ME_QUERY } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.dfc.new';
import { useAsyncBulkGenerateQuizMeMutation, useAsyncBulkGetQuizMeLazyQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import { buildLessonDifficultyTaskIds, computeQuizMePollStatus, normalizeDifficulty, PRE_ASSESSMENT_DIFFICULTY, selectPollTaskByTaskId, selectPollTasksByTaskIds, useQuizMeSession } from '../utils/quizMeUtils.migrated';
import { DIFFICULTY_LEVELS } from '../interactiveEducationConstants.migrated';
import { useSingleVideoQuizRegeneration } from './useSingleVideoQuizRegeneration.migrated';
import { useLessonPreQuizSession } from '../../preAssessmentQuiz/hooks/useLessonPreQuiz.migrated';
import { useAllVideosCaptionsExist } from '../../video/videoHooks.migrated';
import { queryDataFromCache, updateCache } from '../../../utils/DataFetchingClientCache.new';
import { handleApolloErrors } from '../../../utils/errorUtils.legacy';
// Poll until the bulk quiz is ready, giving up after ~60s (MAX_POLL_COUNT × POLL_INTERVAL_MS).
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_COUNT = 20;
const resolveAggregateStatus = (aggregateStatus, taskStatus) => {
  if (taskStatus === 'FAILED' || aggregateStatus === 'FAILED') {
    return 'FAILED';
  }
  if (taskStatus !== 'COMPLETED') {
    return taskStatus;
  }
  return aggregateStatus;
};
/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useLessonQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useLessonQuizMeVideoRequestParams = ({
  contents
}) => {
  const videoContentIds = useMemo(() => {
    var _contents$filter$map;
    return (_contents$filter$map = contents === null || contents === void 0 ? void 0 : contents.filter(c => c.__typename === VideoAssetTypename).map(v => v.id)) !== null && _contents$filter$map !== void 0 ? _contents$filter$map : [];
  }, [contents]);
  const {
    captionsExistMap,
    allVideosHaveCaptions,
    loading
  } = useAllVideosCaptionsExist({
    videoContentIds
  });
  const requestParams = useMemo(() => {
    if (!captionsExistMap || !contents) return undefined;
    const requests = contents.filter(content => content.__typename === VideoAssetTypename && captionsExistMap[String(content.id)] === true && !!content.reference && !!content.hubId).map(content => ({
      contentPortalId: content.hubId,
      videoId: Number(content.reference),
      outputLanguage: content.language,
      lessonContentId: Number(content.id)
    }));
    return requests.length > 0 ? requests : undefined;
  }, [captionsExistMap, contents]);
  return {
    requestParams,
    captionsExistMap,
    allVideosHaveCaptions,
    loading
  };
};
const useBulkQuizGeneration = ({
  videos,
  contentIds,
  hasExistingTasks,
  onGenerationCompleted,
  onPollDataUpdate,
  triggerGeneration = true
}) => {
  const {
    aiAccessControls
  } = useGetAiAccessControls();
  const isGenerativeAiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const [pollCount, setPollCount] = useState(0);
  const [quizMeLoading, setQuizMeLoading] = useState(false);
  const [quizMeError, setQuizMeError] = useState();
  const contentIdsKey = contentIds.join('-');
  const prevContentIdsKeyRef = useRef(null);
  const [getQuizMeLazy, {
    data: pollData,
    loading: queryLoading,
    error: pollError,
    stopPolling,
    networkStatus
  }] = useAsyncBulkGetQuizMeLazyQuery({
    notifyOnNetworkStatusChange: true,
    pollInterval: POLL_INTERVAL_MS,
    onError: err => {
      if (pollCount < 2) {
        setQuizMeError(err);
        handleApolloErrors({
          error: err,
          loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizMeGetQuizError'
        });
      }
    }
  });
  const {
    isPolling,
    quizReady,
    retryGeneration
  } = computeQuizMePollStatus({
    networkStatus,
    queryLoading,
    pollData,
    pollError
  });
  const pollEffectRef = useRef({
    quizReady,
    retryGeneration,
    pollCount,
    onPollDataUpdate
  });
  pollEffectRef.current = {
    quizReady,
    retryGeneration,
    pollCount,
    onPollDataUpdate
  };
  useEffect(() => {
    const {
      quizReady: isQuizReady,
      retryGeneration: shouldRetry,
      pollCount: currentPollCount,
      onPollDataUpdate: handlePollDataUpdate
    } = pollEffectRef.current;
    if (!isQuizReady && isPolling) {
      setPollCount(prev => prev + 1);
      setQuizMeLoading(true);
    }
    if (currentPollCount > MAX_POLL_COUNT || isQuizReady || shouldRetry) {
      setQuizMeError(currentPollCount > MAX_POLL_COUNT ? {
        message: I18n.text('learningCenter.interactiveEducation.errors.asyncQuizMeGetQuizError'),
        name: 'QuizGenerationError'
      } : undefined);
      stopPolling();
      setPollCount(0);
      setQuizMeLoading(false);
    }
    if (pollData) {
      const isTimedOut = currentPollCount > MAX_POLL_COUNT;
      handlePollDataUpdate(pollData, isTimedOut);
    }
  }, [stopPolling, pollData, pollError, isPolling]);
  const [asyncGenerateQuizMe, {
    loading: generateLoading,
    error: generateError,
    data: generateData,
    reset: resetMutation
  }] = useAsyncBulkGenerateQuizMeMutation({
    onError: err => handleApolloErrors({
      error: err,
      loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizGenerationError'
    }),
    onCompleted: quizData => {
      const taskIds = onGenerationCompleted(quizData.result);
      void getQuizMeLazy({
        variables: {
          taskIds
        }
      });
    }
  });
  const handleTriggerGeneration = useCallback(() => {
    if (videos && !isAcceptanceTest()) {
      void asyncGenerateQuizMe({
        variables: {
          videos
        }
      });
    }
  }, [asyncGenerateQuizMe, videos]);
  const resetForRetry = useCallback(() => {
    resetMutation();
    setPollCount(0);
    setQuizMeLoading(false);
    setQuizMeError(undefined);
  }, [resetMutation]);
  const shouldTriggerQuizGeneration = useMemo(() => triggerGeneration && !hasExistingTasks && !generateLoading && !generateError && !generateData, [generateData, generateError, generateLoading, hasExistingTasks, triggerGeneration]);
  useEffect(() => {
    if ((shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled) {
      handleTriggerGeneration();
    }
  }, [handleTriggerGeneration, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  useEffect(() => {
    const prevKey = prevContentIdsKeyRef.current;
    const isInitialLoad = prevKey === null;
    prevContentIdsKeyRef.current = contentIdsKey;
    if (isInitialLoad || contentIdsKey === '' || contentIdsKey === prevKey) {
      return;
    }
    setPollCount(0);
    setQuizMeLoading(false);
    setQuizMeError(undefined);
    const isHandledByGenerationEffect = (shouldTriggerQuizGeneration || retryGeneration) && isGenerativeAiEnabled;
    if (!isHandledByGenerationEffect && isGenerativeAiEnabled && !hasExistingTasks) {
      handleTriggerGeneration();
    }
  }, [contentIdsKey, handleTriggerGeneration, hasExistingTasks, isGenerativeAiEnabled, retryGeneration, shouldTriggerQuizGeneration]);
  return {
    generateQuizLoading: generateLoading || quizMeLoading || queryLoading,
    generateQuizError: !!(generateError || quizMeError),
    quizReady,
    retryGeneration,
    data: pollData,
    regenerate: handleTriggerGeneration,
    resetForRetry
  };
};
/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/interactiveEducation/hooks/useLessonQuizMe.ts
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export const useLessonQuizMe = ({
  lesson
}) => {
  const {
    requestParams,
    captionsExistMap
  } = useLessonQuizMeVideoRequestParams({
    contents: lesson === null || lesson === void 0 ? void 0 : lesson.content
  });
  const {
    getQuizState,
    deleteQuizState,
    initialiseQuiz,
    setQuizMeTasks,
    setQuizMeStatus,
    setDifficultyTasks
  } = useQuizMeSession();
  const {
    initializeLessonPreQuiz,
    clearPreQuizTasks,
    setPreQuizTasks,
    setPreQuizStatus
  } = useLessonPreQuizSession();
  const contentIds = useMemo(() => {
    var _requestParams$map;
    return (_requestParams$map = requestParams === null || requestParams === void 0 ? void 0 : requestParams.map(request => request.lessonContentId)) !== null && _requestParams$map !== void 0 ? _requestParams$map : [];
  }, [requestParams]);
  const difficultyExpandedVideos = useMemo(() => requestParams ? DIFFICULTY_LEVELS.flatMap(difficulty => requestParams.map(param => Object.assign({}, param, {
    difficulty
  }))) : undefined, [requestParams]);
  const hasExistingTasks = useMemo(() => {
    if (contentIds.length === 0) {
      return false;
    }
    const allCompleted = contentIds.every(id => {
      var _getQuizState;
      return ((_getQuizState = getQuizState(id, 'LESSON')) === null || _getQuizState === void 0 ? void 0 : _getQuizState.quizMeStatus) === 'COMPLETED';
    });
    if (!allCompleted) return false;
    const cachedTaskIds = buildLessonDifficultyTaskIds(contentIds, id => getQuizState(id, 'LESSON'));
    if (cachedTaskIds.length === 0) return false;
    const cached = queryDataFromCache(ASYNC_BULK_GET_QUIZ_ME_QUERY, {
      taskIds: cachedTaskIds
    });
    return !!(cached !== null && cached !== void 0 && cached.tasks) && cached.tasks.length === cachedTaskIds.length && cached.tasks.every(task => task.status === 'COMPLETED');
  }, [contentIds, getQuizState]);
  const updatePreQuizGeneration = useCallback(taskMap => {
    if (!lesson) {
      return;
    }
    setPreQuizTasks({
      lessonId: lesson.id,
      tasks: taskMap
    });
    setPreQuizStatus({
      lessonId: lesson.id,
      status: 'PROCESSING'
    });
  }, [lesson, setPreQuizStatus, setPreQuizTasks]);
  const onGenerationCompleted = useCallback(data => {
    if (!requestParams || !lesson || !difficultyExpandedVideos) {
      return [];
    }
    requestParams.forEach(request => {
      initialiseQuiz({
        contentType: 'LESSON',
        contentId: request.lessonContentId
      });
      setQuizMeStatus({
        contentType: 'LESSON',
        contentId: request.lessonContentId,
        quizMeStatus: 'PROCESSING'
      });
    });
    initializeLessonPreQuiz({
      lessonId: lesson.id,
      totalQuestions: 0,
      generationStatus: 'PENDING'
    });
    const videoIdToContentId = new Map();
    requestParams.forEach(request => {
      if (request.lessonContentId !== undefined) {
        videoIdToContentId.set(request.videoId, request.lessonContentId);
      }
    });
    const taskIds = [];
    const preQuizTasks = {};
    data.tasks.forEach(task => {
      const difficulty = normalizeDifficulty(task.difficulty);
      const videoContentId = videoIdToContentId.get(task.videoId);
      if (!difficulty || videoContentId === undefined) return;
      setDifficultyTasks({
        contentType: 'LESSON',
        contentId: videoContentId,
        difficultyTasks: {
          [difficulty]: task.taskId
        }
      });

      // Also store INTERMEDIATE in the legacy tasks field so pre-assessment keeps working.
      if (difficulty === PRE_ASSESSMENT_DIFFICULTY) {
        setQuizMeTasks({
          contentType: 'LESSON',
          videoContentId,
          tasks: task.taskId
        });
        preQuizTasks[videoContentId] = task.taskId;
      }
      taskIds.push(task.taskId);
    });
    updatePreQuizGeneration(preQuizTasks);
    return taskIds;
  }, [difficultyExpandedVideos, initializeLessonPreQuiz, initialiseQuiz, lesson, requestParams, setDifficultyTasks, setQuizMeStatus, setQuizMeTasks, updatePreQuizGeneration]);
  const expandedContentIds = useMemo(() => difficultyExpandedVideos ? difficultyExpandedVideos.map(v => v.lessonContentId) : [], [difficultyExpandedVideos]);
  const onPollDataUpdate = useCallback((pollData, isTimedOut) => {
    if (!lesson || !pollData.tasks || contentIds.length === 0) return;
    const effectiveStatus = task => isTimedOut && task.status !== 'COMPLETED' ? 'FAILED' : task.status;

    // Pre-assessment (INTERMEDIATE) taskId per contentId.
    const videoTaskIds = contentIds.map(id => {
      var _getQuizState2;
      return (_getQuizState2 = getQuizState(id, 'LESSON')) === null || _getQuizState2 === void 0 ? void 0 : _getQuizState2.tasks;
    });
    contentIds.forEach((contentId, index) => {
      const task = selectPollTaskByTaskId(pollData.tasks, videoTaskIds[index]);
      if (task) {
        setQuizMeStatus({
          contentType: 'LESSON',
          contentId,
          quizMeStatus: effectiveStatus(task)
        });
      }
    });
    const intermediateTaskIds = videoTaskIds.filter(taskId => !!taskId);
    if (intermediateTaskIds.length !== contentIds.length) return;
    const intermediateTasks = selectPollTasksByTaskIds(pollData.tasks, intermediateTaskIds);
    if (intermediateTasks.length !== contentIds.length) return;
    const preQuizStatus = intermediateTasks.reduce((aggregate, task) => resolveAggregateStatus(aggregate, effectiveStatus(task)), 'COMPLETED');
    setPreQuizStatus({
      lessonId: lesson.id,
      status: preQuizStatus
    });
    if (!intermediateTasks.every(task => task.status === 'COMPLETED')) return;

    // Also cache under INTERMEDIATE taskIds — pre-quiz queries those keys only.
    updateCache(ASYNC_BULK_GET_QUIZ_ME_QUERY, () => ({
      tasks: intermediateTasks
    }), {
      taskIds: intermediateTaskIds
    });
  }, [lesson, setPreQuizStatus, setQuizMeStatus, contentIds, getQuizState]);
  const {
    generateQuizLoading,
    generateQuizError,
    quizReady,
    retryGeneration,
    regenerate,
    resetForRetry
  } = useBulkQuizGeneration({
    videos: difficultyExpandedVideos,
    contentIds: expandedContentIds,
    hasExistingTasks,
    onGenerationCompleted,
    onPollDataUpdate
  });
  const retryQuizGeneration = useCallback(() => {
    if (lesson) {
      clearPreQuizTasks(lesson.id);
    }
    contentIds.forEach(id => deleteQuizState({
      contentType: 'LESSON',
      contentId: id
    }));
    resetForRetry();
    regenerate();
  }, [lesson, clearPreQuizTasks, contentIds, deleteQuizState, resetForRetry, regenerate]);
  const getAllTaskIds = useCallback(() => buildLessonDifficultyTaskIds(contentIds, id => getQuizState(id, 'LESSON')), [contentIds, getQuizState]);
  const {
    regenerateSingleVideo
  } = useSingleVideoQuizRegeneration({
    contentType: 'LESSON',
    getAllTaskIds
  });
  const regenerateSingleVideoQuiz = useCallback((contentId, difficulty) => {
    if (!requestParams) {
      return;
    }
    const request = requestParams.find(r => r.lessonContentId === contentId);
    if (!request) {
      return;
    }
    const videoRequest = _objectWithoutPropertiesLoose(request, _excluded);
    regenerateSingleVideo({
      videoRequest,
      contentId,
      difficulty
    });
  }, [requestParams, regenerateSingleVideo]);
  return {
    captionsExistMap,
    generateQuizLoading,
    generateQuizError,
    quizReady,
    retryGeneration,
    regenerate,
    regenerateSingleVideoQuiz,
    retryQuizGeneration
  };
};